import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { ROLES } from '../../constants/rolePermissions';

const OLDER_COMMITS_TOOLTIP_ID = 'older-commits-excluded-tooltip';
const OLDER_COMMITS_TOOLTIP_TEXT =
  'These commits are from the period between your first integration and the start of your paid plan. They were not analyzed because your free-plan token allowance was not enough. You can include them with Historical Analysis.';

const formatLastCheck = timestamp => {
  if (!timestamp) {
    return '-';
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
    .format(date)
    .replace(',', '');
};

const toCount = value => {
  const count = Number(value || 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

const formatNumber = value => new Intl.NumberFormat(undefined).format(value);

const isPhaseComplete = phase => {
  const total = toCount(phase?.totalCommits);
  return (
    total > 0 &&
    toCount(phase?.completedCommits) + toCount(phase?.failedCommits) >= total
  );
};

export const getHistoricalAnalysisStatusText = progress => {
  if (!progress) {
    return '';
  }

  const total = toCount(progress.totalCommits);
  const preprocessed = Math.min(total, toCount(progress.preprocessedCommits));
  const formatProgress = current =>
    `${formatNumber(Math.max(0, current))} / ${formatNumber(total)}`;

  if (total > 0 && preprocessed < total) {
    return `Preparing commit data ${formatProgress(preprocessed)}`;
  }

  if (!isPhaseComplete(progress.effort)) {
    return `Effort analysis ${formatProgress(
      toCount(progress?.effort?.submittedCommits)
    )}`;
  }

  if (!isPhaseComplete(progress.categorization)) {
    return `Categorization ${formatProgress(
      toCount(progress?.categorization?.submittedCommits)
    )}`;
  }

  if (!isPhaseComplete(progress.aiLeverage)) {
    return `AI leverage ${formatProgress(
      toCount(progress?.aiLeverage?.submittedCommits)
    )}`;
  }

  return `Finalizing results ${formatProgress(
    toCount(progress?.finalizing?.completedCommits)
  )}`;
};

export const canManageHistoricalAnalysis = role =>
  [ROLES.ACCOUNT_ADMIN].includes(role);

export const getAnalysisStatusSummary = ({
  pendingCount,
  excludedCount,
  role
} = {}) => {
  const normalizedPendingCount = toCount(pendingCount);
  const normalizedExcludedCount = toCount(excludedCount);

  return {
    pendingCount: normalizedPendingCount,
    excludedCount: normalizedExcludedCount,
    hasPendingCommits: normalizedPendingCount > 0,
    hasExcludedCommits: normalizedExcludedCount > 0,
    canManageHistoricalAnalysis: canManageHistoricalAnalysis(role)
  };
};

const AnalysisStatusChip = () => {
  const { userInfo, user, userInfoLastFetchedAt } = useSelector(state => state);
  const analysisInfo = userInfo?.user || user || {};
  const {
    pendingCount,
    excludedCount,
    hasPendingCommits,
    hasExcludedCommits,
    canManageHistoricalAnalysis: canManageHistoricalAnalysisForUser
  } = getAnalysisStatusSummary({
    pendingCount: analysisInfo?.pendingAnalysisCommitCount,
    excludedCount: analysisInfo?.excludedAnalysisCommitCount,
    role: analysisInfo?.role
  });
  const historicalAnalysisStatus = getHistoricalAnalysisStatusText(
    analysisInfo?.historicalAnalysisProgress
  );
  const lastCheck = formatLastCheck(userInfoLastFetchedAt);
  const formattedPendingCount = formatNumber(pendingCount);
  const showUpgradeCta =
    hasPendingCommits &&
    analysisInfo?.freePlanAnalysisQuotaExceeded === true &&
    canManageHistoricalAnalysisForUser;
  const formattedExcludedCount = formatNumber(excludedCount);
  const showHistoricalAnalysisColumn =
    hasExcludedCommits || Boolean(historicalAnalysisStatus);

  return (
    <div className="hidden items-stretch justify-end gap-4 text-right leading-[1.2] lg:flex">
      {showHistoricalAnalysisColumn && (
        <div
          role="group"
          aria-label="Historical analysis"
          className="flex flex-col justify-center"
        >
          {hasExcludedCommits && (
            <div className="group relative inline-flex self-end">
              <span
                tabIndex={0}
                aria-describedby={OLDER_COMMITS_TOOLTIP_ID}
                className="inline-flex cursor-help items-center gap-1 whitespace-nowrap rounded-sm text-xs font-medium text-amber-600 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 focus-visible:ring-offset-1 dark:text-amber-400 dark:focus-visible:ring-offset-boxdark"
              >
                <WarningAmberOutlinedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 15 }}
                />
                <span>Older commits excluded: {formattedExcludedCount}</span>
              </span>
              <span
                id={OLDER_COMMITS_TOOLTIP_ID}
                role="tooltip"
                className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-slate-100 dark:text-slate-950"
              >
                {OLDER_COMMITS_TOOLTIP_TEXT}
              </span>
            </div>
          )}
          {hasExcludedCommits && canManageHistoricalAnalysisForUser && (
            <button
              type="button"
              onClick={() => {
                window.location.href = '/settings/historical-analysis';
              }}
              className="mt-1 text-right text-[11px] font-medium text-primary hover:underline"
            >
              Analyze older commits with Historical Analysis
            </button>
          )}
          {historicalAnalysisStatus && (
            <button
              type="button"
              onClick={() => {
                window.location.href = '/settings/historical-analysis';
              }}
              className="mt-1 text-right text-[11px] font-medium text-primary hover:underline"
            >
              Historical analysis: {historicalAnalysisStatus}
            </button>
          )}
        </div>
      )}

      <div
        role="group"
        aria-label="Current analysis"
        className={`flex flex-col justify-center ${
          showHistoricalAnalysisColumn
            ? 'border-l border-stroke/70 pl-4 dark:border-strokedark'
            : ''
        }`}
      >
        <p
          className={`whitespace-nowrap text-xs font-medium ${
            hasPendingCommits ? 'text-primary' : 'text-bodydark2'
          }`}
        >
          {hasPendingCommits
            ? `Waiting for analysis: ${formattedPendingCount} commits`
            : 'No commits waiting for analysis'}
        </p>
        <p className="text-[11px] text-bodydark2">Last check: {lastCheck}</p>
        {showUpgradeCta && (
          <Link
            to="/settings/plan-configuration"
            className="mt-1 self-end whitespace-nowrap text-[11px] font-medium text-primary hover:underline focus-visible:underline"
          >
            Upgrade to continue analysis
          </Link>
        )}
      </div>
    </div>
  );
};

export default AnalysisStatusChip;
